// This file exports the merchant terms and conditions markdown content
// It's a workaround for Create React App which doesn't support direct .md imports

export const merchantTermsMarkdown = `# Merchant Terms and Conditions

Last updated: January 2025

## Introduction

These Merchant Terms and Conditions ("Terms") govern your use of Pale's services as a merchant ("Merchant", "you", or "your"). By accessing or using our services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not use our services.

## Definitions

- **"Pale"**, **"we"**, **"us"**, or **"our"** refers to Pale and its services
- **"Services"** refers to all products, services, and features provided by Pale
- **"Merchant"** refers to any individual or entity that uses Pale's services for business purposes
- **"Platform"** refers to Pale's website, applications, and related services

## Acceptance of Terms

By registering for or using Pale's services, you acknowledge that you have read, understood, and agree to be bound by these Terms and all applicable laws and regulations. If you do not agree with any part of these Terms, you must not use our services.

## Merchant Account

### Registration

To use our services as a merchant, you must:

- Provide accurate, current, and complete information during registration
- Maintain and promptly update your account information
- Maintain the security of your account credentials
- Accept responsibility for all activities that occur under your account
- Be at least 18 years of age or the age of majority in your jurisdiction

### Account Security

You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to immediately notify us of any unauthorized use of your account.

## Use of Services

### Permitted Use

You may use our services only for lawful business purposes and in accordance with these Terms. You agree not to:

- Violate any applicable laws or regulations
- Infringe upon the rights of others
- Transmit any harmful, offensive, or illegal content
- Interfere with or disrupt the services or servers
- Attempt to gain unauthorized access to any part of the services
- Use the services to compete with Pale or to build a competing service

### Service Availability

We strive to provide reliable services but do not guarantee uninterrupted or error-free operation. We reserve the right to modify, suspend, or discontinue any part of our services at any time with or without notice.

## Fees and Payment

### Fees

You agree to pay all fees associated with your use of our services as described in our pricing plans. Fees are subject to change with reasonable notice.

### Payment Terms

- Payment is due according to the terms specified in your selected plan
- All fees are non-refundable unless otherwise stated
- We reserve the right to suspend or terminate your account for non-payment
- You are responsible for any taxes applicable to your use of our services

## Intellectual Property

### Our Rights

All content, features, and functionality of our services, including but not limited to text, graphics, logos, and software, are owned by Pale or its licensors and are protected by copyright, trademark, and other intellectual property laws.

### Your Rights

You retain ownership of any content you provide to us. By using our services, you grant us a license to use, store, and process your content as necessary to provide the services.

## Data and Privacy

### Data Collection

We collect and process data as described in our Privacy Policy. By using our services, you consent to our data practices as outlined in the Privacy Policy.

### Data Security

We implement reasonable security measures to protect your data, but we cannot guarantee absolute security. You are responsible for maintaining appropriate security measures for your own systems.

## Limitation of Liability

To the maximum extent permitted by law:

- Our services are provided "as is" and "as available" without warranties of any kind
- We disclaim all warranties, express or implied, including merchantability and fitness for a particular purpose
- We shall not be liable for any indirect, incidental, special, consequential, or punitive damages
- Our total liability shall not exceed the amount you paid us in the twelve months preceding the claim

## Indemnification

You agree to indemnify, defend, and hold harmless Pale and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:

- Your use of our services
- Your violation of these Terms
- Your violation of any rights of another party
- Your violation of any applicable laws or regulations

## Termination

### By You

You may terminate your account at any time by contacting us or using the account termination features in our services.

### By Us

We may suspend or terminate your account immediately if:

- You violate these Terms
- You fail to pay required fees
- We determine that your use of the services poses a risk to us or other users
- We discontinue the services

### Effect of Termination

Upon termination:

- Your right to use the services will immediately cease
- We may delete your account and data
- All provisions of these Terms that by their nature should survive termination will survive

## Modifications to Terms

We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of the services after such modifications constitutes acceptance of the updated Terms.

## Dispute Resolution

### Governing Law

These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Pale operates, without regard to its conflict of law provisions.

### Dispute Resolution Process

Any disputes arising from these Terms or your use of our services shall be resolved through:

1. Good faith negotiation
2. Mediation, if negotiation fails
3. Binding arbitration or litigation, as applicable under local law

## General Provisions

### Entire Agreement

These Terms, together with our Privacy Policy, constitute the entire agreement between you and Pale regarding the use of our services.

### Severability

If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.

### Waiver

Our failure to enforce any provision of these Terms does not constitute a waiver of that provision.

### Assignment

You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms without restriction.

## Contact Information

If you have any questions about these Merchant Terms and Conditions, please contact us through our website or the contact information provided in our services.
`;
